import { getType } from 'typesafe-actions';
import { setSearchQuery, setSortOrder, setStores } from '../actions';
import * as R from 'ramda';
import { DirectoryStoreState, SortOrder, Store } from '../types';
import { createSelector } from 'reselect';
import {
  sortByNumberAsc,
  sortByNumberDesc,
  sortByStoreAsc,
  sortByStoreDesc,
} from '../utils/stores';
import { QueryStringState } from './queryString';

const { createReducer } = require('redux-ramda');

export interface StoresState {
  storesIndex: Store[];
  searchQuery: string;
  sortOrder: SortOrder;
}
const initialState: StoresState = {
  storesIndex: [],
  searchQuery: '',
  sortOrder: SortOrder.storesAsc,
};

export const stores = createReducer(initialState, [
  [getType(setStores), R.assoc('storesIndex')],
  [getType(setSortOrder), R.assoc('sortOrder')],
  [getType(setSearchQuery), R.assoc('searchQuery')],
]);

export const getFilteredStores = createSelector<
  DirectoryStoreState,
  StoresState['storesIndex'],
  StoresState['sortOrder'],
  StoresState['searchQuery'],
  Store[]
>(
  state => state.stores.storesIndex,
  state => state.stores.sortOrder,
  state => state.stores.searchQuery,
  (storesIndex, sortOrder, searchQuery) => {
    if (searchQuery) {
      return getSortedStores(sortOrder, storesIndex.filter(isSearchQuery(searchQuery)));
    }
    return getSortedStores(sortOrder, storesIndex);
  }
);

export const getSortedStores = (sortOrder: SortOrder, stores: Store[]) => {
  if (sortOrder === SortOrder.storesDesc) {
    return sortByStoreDesc(stores);
  } else if (sortOrder === SortOrder.numberAsc) {
    return sortByNumberAsc(stores);
  }
  if (sortOrder === SortOrder.numberDesc) {
    return sortByNumberDesc(stores);
  }
  return sortByStoreAsc(stores);
};

const isSearchQuery = R.curry((searchQuery: string, store: Store) => {
  const searchableStr = R.join(' ', R.props(['store', 'number', 'store_api_uuid'])(store));
  return nameRegExpTest(searchableStr, R.trim(searchQuery));
});

export const nameRegExpTest = (name: string, searchQuery: string) => {
  return new RegExp(String(searchQuery).toLowerCase()).test(name.toLowerCase());
};

export const getPreloadedStore = createSelector<
  DirectoryStoreState,
  StoresState['storesIndex'],
  QueryStringState['sloc'],
  Store | undefined
>(
  state => state.stores.storesIndex,
  state => state.queryString.sloc,
  (storesIndex, sloc) => {
    if (sloc) {
      return getStoreBySloc(storesIndex, sloc);
    }
  }
);

export const getStoreBySloc = (stores: Store[], sloc: string) => {
  return stores.find(R.propEq('sloc', sloc));
};

export const getStoreIdBySloc = (sloc: string) => (state: DirectoryStoreState) => {
  const store = state.stores.storesIndex.find(R.propEq('sloc', sloc));
  if (store) {
    return store.id;
  }
};
